import { baseApi } from './baseApi';

export interface UserProfileResponse {
  id: string;
  name: string;
  phone?: string;
  avatarUrl?: string;
  coverImageUrl?: string;
  bio?: string;
  address?: string;
  /** male | female | other; empty when the customer did not say. */
  gender?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthMeResponse {
  id: string;
  email: string;
  phone?: string;
  name: string;
  avatarUrl?: string;
  role: string;
  tenantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateProfileRequest {
  name?: string;
  phone?: string;
  avatarUrl?: string;
  bio?: string;
  address?: string;
  gender?: string | null;
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserProfile: builder.query<UserProfileResponse, void>({
      query: () => '/profile/me',
      providesTags: ['UserProfile'],
    }),
    getAuthMe: builder.query<AuthMeResponse, void>({
      query: () => '/auth/me',
      providesTags: ['UserProfile'],
    }),
    updateUserProfile: builder.mutation<UserProfileResponse, UpdateProfileRequest>({
      query: (body) => ({
        url: '/profile/me',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['UserProfile'],
    }),
    uploadAvatar: builder.mutation<UserProfileResponse, { uri: string; fileName?: string; mimeType?: string }>({
      queryFn: async ({ uri, fileName, mimeType }, { getState }) => {
        try {
          const token = (getState() as any)?.auth?.token;
          const formData = new FormData();
          if (typeof window !== 'undefined' && /^(blob:|data:|https?:)/.test(uri)) {
            // In a browser the picked photo is a real file: read it back and send it as one.
            const blob = await (await fetch(uri)).blob();
            formData.append('file', blob, fileName || 'avatar.jpg');
          } else {
            formData.append('file', {
              uri,
              name: fileName || 'avatar.jpg',
              type: mimeType || 'image/jpeg',
            } as any);
          }

          const { API_BASE_URL } = await import('./baseApi');
          const res = await fetch(`${API_BASE_URL}/profile/avatar`, {
            method: 'POST',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: formData,
          });

          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            return {
              error: {
                status: res.status,
                data: errData,
              },
            };
          }

          const data = await res.json();
          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              data: err?.message || 'Failed to upload photo',
              error: err?.message || 'Failed to upload photo',
            },
          };
        }
      },
      invalidatesTags: ['UserProfile'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserProfileQuery,
  useGetAuthMeQuery,
  useUpdateUserProfileMutation,
  useUploadAvatarMutation,
} = userApi;
